'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { GeneratingDots } from '@/components/glitch-ui/generating-dots';
import { StepsIndicator } from '@/components/glitch-ui/steps-indicator';
import { ArrowLeft, ArrowRight, PencilLine, Search, Sparkles, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';

import { SourceCampaignPicker } from '@/components/campaign/source-campaign-picker';
import { CampaignLocations } from '@/components/campaign/onboarding/campaign-onboarding-locations';
import { useToast } from '@/hooks/useToast';
import useLoadingOverlay from '@/hooks/useLoadingOverlay';
import { organizationDataAtom } from '@/store';
import {
  businessQueryOptions,
  campaignsQueryOptions,
  campaignKeywordsWithPerfQueryOptions,
  useCreateOptimisationCampaignMutation,
} from '@/lib/query-options';
import type { CampaignKeywordWithPerf, Location, CampaignProximityInput } from '@/lib/types';
import { setCampaignProximityTargets } from '@/lib/apis';

export const Route = createFileRoute('/_authenticated/new/_layout/campaign/hipo')({
  component: HipoCampaign,
});

type HipoTab = 'campaign' | 'manual';

// Step 1 of the wizard (#4818): the two ways to seed a HiPo campaign, shown as
// selectable rows (same affordance as the campaign-type picker).
const HIPO_METHODS = [
  {
    id: 'campaign',
    icon: TrendingUp,
    title: 'From campaigns',
    description: 'Pick the top-performing keywords from one of your existing campaigns.',
  },
  {
    id: 'manual',
    icon: PencilLine,
    title: 'From keywords',
    description: 'Add your own keywords and target locations manually.',
  },
] as const;

// Mirror Google Ads' Search keyword limits — both apply independently.
// Keep in sync with backend/app/campaign/schemas.py
// (KEYWORD_MAX_LENGTH / KEYWORD_MAX_WORDS).
const MANUAL_KEYWORD_MAX_LENGTH = 80;
const MANUAL_KEYWORD_MAX_WORDS = 10;

type ParsedManualKeywords = {
  valid: string[];
  duplicates: number;
  tooLong: number;
  tooManyWords: number;
};

/** Parse a newline-separated textarea blob into a sanitised keyword list.
 * Trim each line, collapse internal whitespace, drop empties, reject anything
 * over MANUAL_KEYWORD_MAX_LENGTH characters or MANUAL_KEYWORD_MAX_WORDS words,
 * and dedupe case-insensitively while preserving order and the first
 * occurrence's original casing. Skipped-row counts are returned for inline UX
 * feedback. */
function parseManualKeywords(text: string): ParsedManualKeywords {
  const seen = new Set<string>();
  const valid: string[] = [];
  let duplicates = 0;
  let tooLong = 0;
  let tooManyWords = 0;
  for (const raw of text.split('\n')) {
    const normalized = raw.trim().replace(/\s+/g, ' ');
    if (!normalized) continue;
    if (normalized.length > MANUAL_KEYWORD_MAX_LENGTH) {
      tooLong += 1;
      continue;
    }
    if (normalized.split(' ').length > MANUAL_KEYWORD_MAX_WORDS) {
      tooManyWords += 1;
      continue;
    }
    const key = normalized.toLowerCase();
    if (seen.has(key)) {
      duplicates += 1;
      continue;
    }
    seen.add(key);
    valid.push(normalized);
  }
  return { valid, duplicates, tooLong, tooManyWords };
}

/** Currency formatter for cost display. We don't have the source campaign's
 * currency on the keyword row itself; default to USD which matches the
 * Google Ads default for our test accounts. The wizard is short-lived and
 * the cost values are zero today (performance metrics aren't stored
 * locally), so this is acceptable until the metrics land. */
const costFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

function formatCost(costMicros: number): string {
  return costFormatter.format(costMicros / 1_000_000);
}

function HipoCampaign() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { toast } = useToast();
  const posthog = usePostHog();
  const organizationData = useAtomValue(organizationDataAtom);

  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  const { data: campaigns, isLoading: campaignsLoading } = useQuery({
    ...campaignsQueryOptions(organizationData?.id),
    enabled: !!organizationData?.id,
  });
  const eligibleSources = (campaigns || []).filter(
    (c) => c.goal === 'prospecting' || c.goal === 'standard' || c.goal === 'onboarding'
  );

  const [activeTab, setActiveTab] = useState<HipoTab>('campaign');
  const [sourceCampaignId, setSourceCampaignId] = useState<number | null>(null);
  const [selectedKeywordIds, setSelectedKeywordIds] = useState<Set<number>>(new Set());
  const [manualText, setManualText] = useState('');
  // Manual tab geo targeting. Pre-fills from business.target_locations as a
  // sensible default (matches prospecting/competitor/brand wizard convention)
  // but the picker is fully editable — user can add, remove, or clear.
  // Submit guard below requires >=1 so a cleared list can't ship.
  const [manualLocations, setManualLocations] = useState<Location[]>([]);
  // #5444: radii chosen on the wizard's Radius tab, saved post-create.
  const [radii, setRadii] = useState<CampaignProximityInput[]>([]);
  // CampaignLocations seeds its internal useState from the `locations` prop
  // only on mount. The seed effect below fires AFTER mount (once the business
  // query resolves), so we'd end up with parent state populated but the
  // picker visibly empty. Flipping this once-only flag changes the key prop
  // on CampaignLocations, forcing a single remount that captures the seeded
  // values as its initial state. Subsequent user edits don't change the key,
  // so focus / typing state inside the picker is preserved.
  const [locationsSeeded, setLocationsSeeded] = useState(false);
  // Stepped wizard (#4818): step 1 picks the method as selectable rows. The
  // campaign path is then 2 steps (method → keywords); the manual path is 3
  // (method → keywords → locations), so each input gets its own focused step.
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // "From campaigns" needs at least one eligible source. With none, that method
  // is disabled — default the selection to "From keywords" so step 1 doesn't sit
  // on a disabled option (#4818).
  const noEligibleSources = !campaignsLoading && eligibleSources.length === 0;
  useEffect(() => {
    if (noEligibleSources && activeTab === 'campaign') {
      setActiveTab('manual');
    }
  }, [noEligibleSources, activeTab]);

  // Fetch source-campaign keywords as soon as a source is picked on the
  // campaign tab. Skipped while the manual tab is active or no source.
  const keywordsEnabled = activeTab === 'campaign' && sourceCampaignId !== null;
  const { data: keywords, isLoading: keywordsLoading } = useQuery(
    campaignKeywordsWithPerfQueryOptions(keywordsEnabled ? sourceCampaignId : null)
  );

  const createMutation = useCreateOptimisationCampaignMutation();
  useLoadingOverlay(createMutation.isPending);

  const pickedSource = useMemo(
    () => eligibleSources.find((c) => c.id === sourceCampaignId),
    [eligibleSources, sourceCampaignId]
  );

  // Reset keyword selection when the source changes — stale ids from a
  // different source must not bleed into the current selection. Manual
  // keywords live on a separate tab and are intentionally preserved.
  const lastAutoSelectedSourceRef = useRef<number | null>(null);
  useEffect(() => {
    setSelectedKeywordIds(new Set());
    lastAutoSelectedSourceRef.current = null;
  }, [sourceCampaignId]);

  // Auto-select recommended keywords on first load for a source campaign.
  // The ref guard prevents re-overriding the user's selection if the query
  // refetches while they're still on the same source.
  useEffect(() => {
    if (!keywords || sourceCampaignId === null) return;
    if (lastAutoSelectedSourceRef.current === sourceCampaignId) return;
    const recommendedIds = new Set(
      keywords.filter((kw) => kw.is_hipo_recommended).map((kw) => kw.id)
    );
    setSelectedKeywordIds(recommendedIds);
    lastAutoSelectedSourceRef.current = sourceCampaignId;
  }, [keywords, sourceCampaignId]);


  // Seed manual-tab locations from the Business's saved target_locations
  // once it loads. Matches prospecting/competitor/brand wizard convention.
  // One-shot via locationsSeeded so a manual clear/edit isn't overwritten on
  // a later business refetch, and so flipping the key on CampaignLocations
  // only happens once.
  useEffect(() => {
    if (locationsSeeded) return;
    if (business?.target_locations !== undefined) {
      if (business.target_locations.length > 0) {
        setManualLocations(business.target_locations);
      }
      setLocationsSeeded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.target_locations]);

  const toggleKeyword = (id: number) => {
    setSelectedKeywordIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const parsedManual = useMemo(() => parseManualKeywords(manualText), [manualText]);

  // Source campaigns without target_locations are blocked at the picker (see SourceCampaignPicker
  // getDisabledReason below) — the backend 400s when source_target_locations is empty on the
  // From-a-campaign path (campaign/router.py:956-971). Disabling at the picker is enough; no
  // extra submit gate needed here.
  const submitValid =
    activeTab === 'campaign'
      ? sourceCampaignId !== null && selectedKeywordIds.size >= 1
      : parsedManual.valid.length >= 1 && manualLocations.length >= 1;

  // Step model (#4818): campaign = 2 steps (method → keywords); manual = 3
  // (method → keywords → locations). CONTINUE off the keywords step requires at
  // least one keyword so the user can't skip ahead to locations empty-handed.
  const totalSteps = activeTab === 'manual' ? 3 : 2;
  const isLastStep = step === totalSteps;
  const canContinue = step === 1 ? true : parsedManual.valid.length >= 1;


  const handleBack = () => {
    // Step back through the flow; from step 1, leave the wizard.
    if (step > 1) {
      setStep((s) => (s - 1) as 1 | 2 | 3);
      return;
    }
    navigate({ to: '/new/campaign' });
  };

  const submitFromCampaignTab = async () => {
    if (!business?.id || !organizationData?.id || !sourceCampaignId || !keywords) return;

    const selectedKeywords = keywords.filter((k) => selectedKeywordIds.has(k.id));
    if (selectedKeywords.length === 0) return;

    posthog?.capture('hipo_campaign_creation_submitted', {
      business_id: business.id,
      source_campaign_id: sourceCampaignId,
      keyword_count: selectedKeywords.length,
      mode: 'from_campaign',
    });
    const response = await createMutation.mutateAsync({
      organizationId: organizationData.id,
      idempotencyKey: crypto.randomUUID(),
      payload: {
        campaign_type: 'hipo',
        source_campaign_id: sourceCampaignId,
        // The source campaign owns the authoritative business_id; orgs with
        // many businesses would otherwise send a mismatched id and trip the
        // backend's source.business_id == body.business_id guard.
        business_id: pickedSource?.business_id ?? business.id,
        target_locations: [],
        budget_amount: 0,
        intent_data: {
          intent_id: null,
          // The backend treats intent_name as the LLM seed for keyword,
          // headline, and description generation. Prefer the user's first
          // selected keyword over the source campaign name (which can be a
          // routing label like "S US 11_gl HiPo" that poisons the LLM).
          intent_name: selectedKeywords[0]?.text ?? pickedSource?.name ?? 'High Performance',
          keywords: selectedKeywords.map((k) => k.text),
        },
      },
    });
    // Navigate straight to the campaign detail page — the detail view
    // renders its own "Your campaign is being generated" progress block
    // while Celery is still building ads/keywords, so no intermediate
    // confirmation page is needed.
    navigate({
      to: '/campaigns/google/$campaignslug',
      params: { campaignslug: response.slug },
    });
  };

  const submitFromManualTab = async () => {
    if (!business?.id || !organizationData?.id) return;
    if (parsedManual.valid.length === 0 || manualLocations.length === 0) return;

    posthog?.capture('hipo_campaign_creation_submitted', {
      business_id: business.id,
      source_campaign_id: null,
      keyword_count: parsedManual.valid.length,
      location_count: manualLocations.length,
      mode: 'manual',
    });
    const response = await createMutation.mutateAsync({
      organizationId: organizationData.id,
      idempotencyKey: crypto.randomUUID(),
      payload: {
        campaign_type: 'hipo',
        // Manual path: backend inherits website_url and googleadsaccount_id
        // from the Business; locations come from the user's picker selection
        // below (NOT the Business default).
        business_id: business.id,
        // Backend's LocationInput schema is strict (extra="forbid"); the full
        // FE Location object carries fields the API rejects. Strip to just
        // what the schema accepts.
        target_locations: manualLocations.map((loc) => ({
          criteria_id: loc.criteria_id,
          name: loc.name,
          target_type: loc.target_type,
        })),
        budget_amount: 0,
        intent_data: {
          intent_id: null,
          // The backend treats intent_name as the LLM seed for keyword,
          // headline, and description generation. Use the user's first
          // typed keyword as the seed; falling back to the generic label
          // means the LLM produces copy anchored to "high performance"
          // instead of the user's actual target keyword.
          intent_name: parsedManual.valid[0] ?? 'High Performance',
          keywords: parsedManual.valid,
        },
      },
    });
    // #5444: persist the wizard's Radius-tab selection on the new campaign.
    if (radii.length > 0 && response.campaign_id) {
      try {
        await setCampaignProximityTargets(response.campaign_id, radii);
      } catch {
        toast({
          description: 'Campaign created, but saving radius targets failed — set them in campaign settings.',
          variant: 'destructive',
        });
      }
    }
    // Navigate straight to the campaign detail page — the detail view
    // renders its own "Your campaign is being generated" progress block
    // while Celery is still building ads/keywords, so no intermediate
    // confirmation page is needed.
    navigate({
      to: '/campaigns/google/$campaignslug',
      params: { campaignslug: response.slug },
    });
  };

  // Synchronous guard against double-click. ``createMutation.isPending`` only flips after a
  // React re-render, but a rapid double-click fires both onClick handlers before that frame, so
  // the button's ``disabled`` attribute alone doesn't prevent duplicate submits. A ref updates
  // immediately and short-circuits the second invocation.
  const isSubmittingRef = useRef(false);

  const handleNext = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      if (activeTab === 'campaign') await submitFromCampaignTab();
      else await submitFromManualTab();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create High Performance Campaign';
      posthog?.capture('hipo_campaign_creation_failed', { error_message: errorMessage });
      toast({ description: errorMessage, variant: 'destructive' });
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    // ``absolute inset-0 overflow-y-auto`` makes the wizard a self-contained scroll viewport
    // inside the ``h-screen`` parent layout (``new/_layout.tsx``). Without this, a long
    // source-campaign keyword list pushed the CREATE/BACK row below the viewport with no way to
    // reach it (#4318). The sticky action row below pins the buttons to the bottom edge of this
    // viewport so they remain visible regardless of how tall the keyword table grows.
    <div className="absolute inset-0 overflow-y-auto pt-8 pb-4">
      <div className="max-w-5xl w-full mx-auto space-y-6">
      <div className="flex justify-center">
        <div className={step === 3 ? 'w-full' : 'w-[60%]'}>
          <h1 className="text-3xl font-bold text-center mb-2">Create a High Performance Campaign</h1>
          <p className="text-gray-700 text-center mb-8">
            Pick keywords from an existing campaign, or enter your own.
          </p>
          {/* Two-step flow (#4818): step 1 picks the method as selectable rows, step 2
              enters the data. Standard StepsIndicator dots above show progress. */}
          <StepsIndicator currentStep={step} totalSteps={totalSteps} />
          <Card className="rounded-lg border bg-white p-6">
            {step === 1 ? (
              <div className="space-y-3">
                {HIPO_METHODS.map((m) => {
                  const Icon = m.icon;
                  const isSelected = activeTab === m.id;
                  const disabled = m.id === 'campaign' && noEligibleSources;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => setActiveTab(m.id)}
                      className={`w-full flex items-center space-x-4 p-4 rounded-lg border-2 transition-colors text-left ${
                        disabled
                          ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                          : isSelected
                            ? 'border-[#290BD9] bg-[#290BD9]/10'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div
                        className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isSelected && !disabled ? 'border-[#290BD9]' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isSelected && !disabled && <div className="w-3 h-3 rounded-full bg-[#290BD9]" />}
                      </div>
                      <div
                        className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected && !disabled ? 'bg-[#290BD9] text-white' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <span
                          className={`font-medium ${
                            isSelected && !disabled ? 'text-[#290BD9]' : 'text-gray-900'
                          }`}
                        >
                          {m.title}
                        </span>
                        <div className="text-sm mt-1 text-gray-500">
                          {disabled
                            ? 'No eligible campaigns yet. Create a Prospecting campaign first.'
                            : m.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : step === 2 && activeTab === 'campaign' ? (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="space-y-4"
                >
                  <SourceCampaignPicker
                    campaigns={eligibleSources}
                    selectedId={sourceCampaignId}
                    onSelect={setSourceCampaignId}
                    isLoading={campaignsLoading}
                    collapseOnSelect
                    getDisabledReason={(c) =>
                      (c.target_locations?.length ?? 0) === 0 ? (
                        <span>
                          No target locations.{' '}
                          <a
                            href={`/campaigns/google/${c.slug}#overview`}
                            className="text-[#290BD9] hover:text-[#2309C5] underline"
                          >
                            Add a location
                          </a>{' '}
                          on this campaign first.
                        </span>
                      ) : null
                    }
                    emptyState={
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Sparkles className="h-12 w-12 text-gray-400 mb-4" strokeWidth={1} />
                        <h3 className="text-lg font-semibold text-gray-500 mb-2">No eligible campaigns</h3>
                        <p className="text-gray-500 max-w-sm">
                          Create a Prospecting campaign first to use as a source for High Performance
                          campaigns.
                        </p>
                      </div>
                    }
                  />

                  {sourceCampaignId !== null && (
                    <>
                      <p className="text-sm text-gray-700">
                        Pick 3-5 keywords for your High Performance campaign. Higher-performing ones
                        give the best results, but you can pick any.
                      </p>

                      {keywordsLoading ? (
                        <div className="flex flex-col items-center justify-center gap-3 py-12">
                          <GeneratingDots count={3} />
                          <p className="text-sm text-gray-500">Loading keywords...</p>
                        </div>
                      ) : !keywords || keywords.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <Search className="h-12 w-12 text-gray-400 mb-4" strokeWidth={1} />
                          <h3 className="text-lg font-semibold text-gray-500 mb-2">No keywords yet</h3>
                          <p className="text-gray-500 max-w-sm">
                            This campaign hasn&apos;t generated any keywords yet. Pick another source
                            campaign or wait for keyword generation to finish.
                          </p>
                        </div>
                      ) : (
                        <div className="border rounded-lg overflow-hidden text-sm">
                          <div className="max-h-[480px] overflow-y-auto">
                            {/* Header sits inside the scroll container so its flex width matches
                                the data rows (scrollbar eats from the same shared width). Sticky
                                so it stays visible while scrolling. Typography matches the standard
                                DataTable header pattern (see glitch-ui/data-table/index.tsx). */}
                            <div className="sticky top-0 z-10 flex items-center bg-gray-50 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="pl-4 pr-2 py-3 w-10" />
                              <div className="pl-2 pr-3 py-3 flex-1">Keyword</div>
                              <div className="px-3 py-3 w-20 whitespace-nowrap">Match</div>
                              <div className="px-3 py-3 w-64 whitespace-nowrap">Performance</div>
                            </div>
                            {keywords.map((kw: CampaignKeywordWithPerf) => {
                              const checked = selectedKeywordIds.has(kw.id);
                              return (
                                <div
                                  key={kw.id}
                                  onClick={() => toggleKeyword(kw.id)}
                                  className={`flex items-center border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors ${
                                    checked ? 'bg-[#290BD9]/10' : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <div className="pl-4 pr-2 py-3 w-10 flex items-center">
                                    <Checkbox
                                      checked={checked}
                                      onCheckedChange={() => toggleKeyword(kw.id)}
                                      onClick={(e) => e.stopPropagation()}
                                      aria-label={`Select keyword ${kw.text}`}
                                    />
                                  </div>
                                  <div className="pl-2 pr-3 py-3 font-medium text-gray-900 flex-1 flex items-center gap-2 min-w-0">
                                    <span className="break-words">{kw.text}</span>
                                    {kw.is_hipo_recommended && (
                                      <span className="inline-flex items-center gap-1 shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#290BD9]/10 text-[#290BD9] border border-[#290BD9]/20">
                                        <Sparkles className="h-2.5 w-2.5" />
                                        Recommended
                                      </span>
                                    )}
                                  </div>
                                  <div className="px-3 py-3 w-20">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                      {kw.match_type}
                                    </span>
                                  </div>
                                  <div className="px-3 py-3 w-64 whitespace-nowrap flex items-center gap-3 text-sm text-gray-900 tabular-nums">
                                    <span>{kw.impressions.toLocaleString()} <span className="text-xs text-gray-500">imp</span></span>
                                    <span>{kw.clicks.toLocaleString()} <span className="text-xs text-gray-500">clicks</span></span>
                                    <span>{formatCost(kw.cost_micros)} <span className="text-xs text-gray-500">cost</span></span>
                                    <span>{kw.conversions.toLocaleString()} <span className="text-xs text-gray-500">conv</span></span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 text-sm text-gray-700">
                            {selectedKeywordIds.size} selected
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </motion.div>
            ) : step === 2 ? (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="space-y-2"
                >
                  <div>
                    <h2 className="text-[20px] font-semibold text-gray-900">Add your keywords</h2>
                    <p className="mt-1 text-base text-gray-700">
                      Keywords run as PHRASE match using your business&apos;s Google Ads account. Add one keyword per line.
                    </p>
                  </div>
                  <Textarea
                    id="hipo-manual-keywords"
                    aria-label="Keywords"
                    value={manualText}
                    onChange={(e) => setManualText(e.target.value)}
                    placeholder={
                      'creator marketing platform\ninfluencer agency africa\ncreator economy software'
                    }
                    className="min-h-[200px] text-gray-900"
                  />
                  {(parsedManual.duplicates > 0 ||
                    parsedManual.tooLong > 0 ||
                    parsedManual.tooManyWords > 0) && (
                    <div className="flex justify-end text-sm">
                      <span className="text-[#B91C1C]">
                        {[
                          parsedManual.duplicates > 0 &&
                            `${parsedManual.duplicates} duplicate${parsedManual.duplicates === 1 ? '' : 's'} skipped`,
                          parsedManual.tooLong > 0 &&
                            `${parsedManual.tooLong} over ${MANUAL_KEYWORD_MAX_LENGTH} chars skipped`,
                          parsedManual.tooManyWords > 0 &&
                            `${parsedManual.tooManyWords} over ${MANUAL_KEYWORD_MAX_WORDS} words skipped`,
                        ]
                          .filter(Boolean)
                          .join(' • ')}
                      </span>
                    </div>
                  )}
                </motion.div>
            ) : (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="space-y-2"
                >
                  <CampaignLocations
                    key={locationsSeeded ? 'seeded' : 'pending'}
                    onNext={() => {}}
                    setLocations={setManualLocations}
                    setStepValid={() => {}}
                    locations={manualLocations}
                    businessId={business?.id}
                    candidates={business?.target_locations ?? []}
                    setRadii={setRadii}
                  />
                </motion.div>
            )}
          </Card>

          {/* ``sticky bottom-0`` pins the action row to the bottom edge of the scrollable wizard
              container so the CREATE button is always visible — even on long source-campaign
              keyword lists or shorter viewports. ``bg-white`` + ``border-t`` give visual
              separation from the content that scrolls behind. No explicit ``z-index`` — DOM
              order already puts the row above its sibling content, and skipping ``z-10`` lets
              the manual-tab country picker dropdown (also ``z-10``) render above the row when
              expanded. (#4318 / #4327) */}
          <div className="mx-auto flex w-full max-w-[614px] pt-4 pb-2 justify-between items-center">
            <Button
              variant="outline"
              size="lg"
              onClick={handleBack}
              className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>BACK</span>
            </Button>
            {isLastStep ? (
              <Button
                size="lg"
                disabled={!submitValid || createMutation.isPending}
                onClick={handleNext}
                className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
              >
                <span>CREATE</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                size="lg"
                disabled={!canContinue}
                onClick={() => setStep((s) => (s + 1) as 1 | 2 | 3)}
                className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
              >
                <span>CONTINUE</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
